import { useEffect, useState, useRef, useCallback } from "react";
import { Map, Source, Layer, useMap } from "react-map-gl/maplibre";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import * as pmtiles from "pmtiles";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";

const PMTILES_PROTOCOL = "pmtiles";
const SOURCE_LAYER = "lines";

const FEATURE_COLORS = {
    line: "#4fc3f7",
    section: "#81c784",
    span: "#ce93d8",
};

const FEATURE_WIDTHS = {
    line: 2,
    section: 1.5,
    span: 0.5,
};

// MapLibre publishes a free public-domain demo style. Internal admin tool only;
// if we ever ship this externally we can point at our own style instead.
const MAP_STYLE = "https://demotiles.maplibre.org/style.json";

// Register the pmtiles:// protocol on maplibre once per module load.
let protocolRegistered = false;
const ensurePmtilesProtocol = () => {
    if (protocolRegistered) return;
    maplibregl.addProtocol(PMTILES_PROTOCOL, new pmtiles.Protocol().tile);
    protocolRegistered = true;
};

const median = (xs) => {
    if (xs.length === 0) return null;
    const s = [...xs].sort((a, b) => a - b);
    const mid = Math.floor(s.length / 2);
    return s.length % 2 ? s[mid] : Math.round((s[mid - 1] + s[mid]) / 2);
};
const percentile = (xs, p) => {
    if (xs.length === 0) return null;
    const s = [...xs].sort((a, b) => a - b);
    const idx = Math.min(s.length - 1, Math.floor((p / 100) * s.length));
    return s[idx];
};

/**
 * MapMetrics — listens to maplibre tile lifecycle events and reports
 * time-to-first-tile, viewport-idle, tiles-loaded, and per-tile latency.
 * Tile fetches happen as HTTP Range requests directly against the signed
 * S3 URL, so latency reflects real network round-trips (NexusIQ-equivalent
 * end-user experience).
 */
function MapMetrics({ onMetrics, tilesUrl }) {
    const { current: mapRef } = useMap();
    const startedAtRef = useRef(performance.now());
    const firstTileAtRef = useRef(null);
    const lastViewportStartRef = useRef(performance.now());
    const tileLatenciesRef = useRef([]);
    const tilesLoadedRef = useRef(0);

    useEffect(() => {
        if (!mapRef) return undefined;
        const map = mapRef.getMap();

        const emit = (viewportIdleMs = null) => {
            onMetrics({
                firstTileMs: firstTileAtRef.current
                    ? firstTileAtRef.current - startedAtRef.current
                    : null,
                tilesLoaded: tilesLoadedRef.current,
                medianLatencyMs: median(tileLatenciesRef.current),
                p95LatencyMs: percentile(tileLatenciesRef.current, 95),
                viewportIdleMs,
            });
        };

        const handleSourceData = (e) => {
            if (e?.sourceId !== "pit-tiles") return;
            if (e?.isSourceLoaded || e?.tile) {
                if (!firstTileAtRef.current) firstTileAtRef.current = performance.now();
                if (e?.tile) tilesLoadedRef.current += 1;
                emit();
            }
        };
        const handleMoveStart = () => {
            lastViewportStartRef.current = performance.now();
        };
        const handleIdle = () => {
            const idleMs = Math.round(performance.now() - lastViewportStartRef.current);
            emit(idleMs);
        };

        map.on("sourcedata", handleSourceData);
        map.on("movestart", handleMoveStart);
        map.on("idle", handleIdle);

        // PerformanceObserver: pull network timings for resource entries that
        // came from the signed S3 host. Gives us real per-Range-request latency.
        const s3Host = (() => {
            try {
                return new URL(tilesUrl).host;
            } catch {
                return null;
            }
        })();
        let perfObserver = null;
        if (typeof PerformanceObserver !== "undefined" && s3Host) {
            perfObserver = new PerformanceObserver((list) => {
                list.getEntries().forEach((entry) => {
                    if (!entry?.name?.includes(s3Host)) return;
                    const dur = Math.round(entry.duration);
                    if (Number.isFinite(dur) && dur > 0) {
                        tileLatenciesRef.current.push(dur);
                        if (tileLatenciesRef.current.length > 200) {
                            tileLatenciesRef.current.shift();
                        }
                    }
                });
            });
            try {
                perfObserver.observe({ entryTypes: ["resource"] });
            } catch {
                perfObserver = null;
            }
        }

        return () => {
            map.off("sourcedata", handleSourceData);
            map.off("movestart", handleMoveStart);
            map.off("idle", handleIdle);
            if (perfObserver) perfObserver.disconnect();
        };
    }, [mapRef, onMetrics, tilesUrl]);

    return null;
}

function MetricsPanel({ metrics }) {
    const fmt = (v, suffix = " ms") => (v == null ? "—" : `${Math.round(v)}${suffix}`);
    return (
        <Paper
            variant="outlined"
            sx={{
                position: "absolute",
                top: 10,
                right: 10,
                zIndex: 5,
                padding: "8px 12px",
                backgroundColor: "rgba(255,255,255,0.92)",
                fontSize: "12px",
                minWidth: "180px",
            }}
        >
            <Typography variant="caption" sx={{ fontWeight: 600, display: "block", marginBottom: "4px" }}>
                Tile performance
            </Typography>
            <div>Time to first tile: {fmt(metrics.firstTileMs)}</div>
            <div>Viewport idle: {fmt(metrics.viewportIdleMs)}</div>
            <div>Tiles loaded: {metrics.tilesLoaded ?? 0}</div>
            <div>Median latency: {fmt(metrics.medianLatencyMs)}</div>
            <div>P95 latency: {fmt(metrics.p95LatencyMs)}</div>
        </Paper>
    );
}

export default function TileQAMap({ tilesUrl, height = 500 }) {
    const [center, setCenter] = useState(null);
    const [zoom, setZoom] = useState(6);
    const [loadError, setLoadError] = useState(null);
    const [metrics, setMetrics] = useState({
        firstTileMs: null,
        viewportIdleMs: null,
        tilesLoaded: 0,
        medianLatencyMs: null,
        p95LatencyMs: null,
    });

    const handleMetrics = useCallback((next) => {
        setMetrics((prev) => ({ ...prev, ...next }));
    }, []);

    // Read the header from the pmtiles archive over HTTP Range — pmtiles only
    // requests the first ~16 KB to learn the center, zoom, and tile directory.
    // Subsequent tile renders Range-fetch additional byte ranges as the viewport
    // moves. No full-file download.
    useEffect(() => {
        if (!tilesUrl) return;
        let cancelled = false;
        (async () => {
            try {
                ensurePmtilesProtocol();
                const archive = new pmtiles.PMTiles(tilesUrl);
                const header = await archive.getHeader();
                if (cancelled) return;
                setCenter({ latitude: header.centerLat, longitude: header.centerLon });
                setZoom(header.centerZoom || 6);
            } catch (err) {
                if (!cancelled) setLoadError(err?.message || String(err));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [tilesUrl]);

    if (loadError) {
        return (
            <Typography variant="body2" color="error" sx={{ textAlign: "center", margin: "16px 0" }}>
                Failed to load tiles: {loadError}
            </Typography>
        );
    }

    if (!center) {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height, gap: 8 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="textSecondary">
                    Loading tiles…
                </Typography>
            </div>
        );
    }

    return (
        <div style={{ width: "100%", height, margin: "16px 0", position: "relative" }}>
            <Map
                initialViewState={{
                    latitude: center.latitude,
                    longitude: center.longitude,
                    zoom,
                }}
                style={{ width: "100%", height: "100%" }}
                mapStyle={MAP_STYLE}
            >
                <Source id="pit-tiles" type="vector" url={`pmtiles://${tilesUrl}`}>
                    {["line", "section", "span"].map((featureType) => (
                        <Layer
                            key={featureType}
                            id={`pit-tiles-${featureType}`}
                            type="line"
                            source-layer={SOURCE_LAYER}
                            filter={["==", ["get", "feature_type"], featureType]}
                            paint={{
                                "line-color": FEATURE_COLORS[featureType],
                                "line-width": FEATURE_WIDTHS[featureType],
                            }}
                        />
                    ))}
                </Source>
                <MapMetrics onMetrics={handleMetrics} tilesUrl={tilesUrl} />
            </Map>
            <MetricsPanel metrics={metrics} />
        </div>
    );
}
